import { useEffect, useRef, useState } from 'react';
import { X, Phone, ArrowRight, Check, Clock } from 'lucide-react';
import axios from 'axios';
import { trackEvent, trackConversion } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'this-week', label: 'This week' },
  { key: 'next-week', label: 'Next week' },
];

/**
 * BookACallSheet — a two-tap booking bottom sheet.
 *
 * Tap 1 in the sticky CTA opens this sheet.
 * Tap 2 confirms the booking (after the visitor drops name + phone).
 *
 * Ships the payload to POST /api/leads/book-a-call which:
 *  - Stores in Mongo `book_a_call_requests`
 *  - Emails the SMG team (hello@) with a formatted "new booking" alert
 *  - Sends a branded confirmation to the visitor when email is provided
 */
export default function BookACallSheet({ open, onClose, source = 'mobile-sticky-cta' }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [win, setWin] = useState('today');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState('');
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    trackEvent('book_a_call_open', { source });
    const t = setTimeout(() => nameRef.current?.focus(), 320);
    return () => { document.body.style.overflow = ''; clearTimeout(t); };
  }, [open, source]);

  // ESC closes on desktop-testing edge
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const reset = () => {
    setStatus('idle');
    setErrMsg('');
    setName('');
    setPhone('');
    setEmail('');
    setWin('today');
    setNote('');
  };

  const close = () => {
    onClose?.();
    // Small delay so the exit animation completes before we wipe fields
    setTimeout(() => { if (status === 'success') reset(); }, 400);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!name.trim() || phone.replace(/\D/g, '').length < 6) {
      setErrMsg('Name and phone are required.');
      return;
    }
    setStatus('sending');
    setErrMsg('');
    try {
      const winLabel = WINDOWS.find((w) => w.key === win)?.label || 'anytime';
      await axios.post(`${API}/leads/book-a-call`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        window: winLabel,
        note: note.trim(),
        source,
      }, { timeout: 20000 });
      setStatus('success');
      trackConversion('book_a_call_submit', 0, 'USD', { source, window: winLabel });
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail || 'Could not send — please try again.');
      trackEvent('form_error', { form: 'book-a-call' });
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        aria-hidden={!open}
        className={`fixed inset-0 z-[310] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="book-call-backdrop"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-label="Book a call with SMG"
        aria-hidden={!open}
        className={`fixed left-0 right-0 bottom-0 z-[320] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? 'translate-y-0 pointer-events-auto' : 'translate-y-full pointer-events-none'
        }`}
        data-testid="book-call-sheet"
      >
        <div
          className="mx-auto w-full max-w-lg bg-[#0a0a0a] border-t border-x border-white/10 rounded-t-2xl shadow-[0_-30px_80px_-20px_rgba(255,61,46,0.35)] overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Drag handle */}
          <div className="pt-2.5 pb-1.5 flex justify-center">
            <span className="w-10 h-1 rounded-full bg-white/20" />
          </div>

          {status === 'success' ? (
            <div className="px-6 pb-8 pt-2 text-center" data-testid="book-call-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#22C55E] flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-white text-2xl leading-tight tracking-tight mb-2">
                You&rsquo;re on the schedule.
              </h3>
              <p className="text-white/65 text-[15px] leading-relaxed mb-6">
                A partner from SMG will call {phone || 'you'} during your window.
                {email ? ' A confirmation is on the way to your inbox.' : ''}
              </p>
              <button
                onClick={close}
                className="inline-flex items-center justify-center w-full min-h-[48px] gap-2 bg-white text-black text-[11px] tracking-[0.3em] px-6 py-3.5 hover:bg-white/90 transition-colors"
                data-testid="book-call-done"
              >
                DONE
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="px-5 pt-2 pb-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-[10px] tracking-[0.3em] text-[#FF3D2E] mb-1.5">— BOOK A CALL</p>
                  <h3 className="font-display text-white text-[26px] leading-[1.05] tracking-tight">
                    Two taps. We&rsquo;ll ring you.
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="shrink-0 w-9 h-9 -mr-1.5 flex items-center justify-center text-white/60 hover:text-white"
                  data-testid="book-call-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Name */}
              <label className="block mb-3">
                <span className="block text-[10px] tracking-[0.3em] text-white/50 mb-1.5">01 · YOUR NAME</span>
                <input
                  ref={nameRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  autoComplete="name"
                  required
                  className="w-full bg-white/[0.04] border border-white/10 focus:border-[#FF3D2E] outline-none px-3 py-3 text-white text-base placeholder:text-white/30 transition-colors"
                  data-testid="book-call-name"
                />
              </label>

              {/* Phone */}
              <label className="block mb-3">
                <span className="block text-[10px] tracking-[0.3em] text-white/50 mb-1.5">02 · MOBILE</span>
                <div className="flex items-center bg-white/[0.04] border border-white/10 focus-within:border-[#FF3D2E] transition-colors">
                  <span className="px-3 text-white/40"><Phone className="w-4 h-4" /></span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(619) 552 4677"
                    autoComplete="tel"
                    inputMode="tel"
                    required
                    className="flex-1 bg-transparent outline-none py-3 pr-3 text-white text-base placeholder:text-white/30"
                    data-testid="book-call-phone"
                  />
                </div>
              </label>

              {/* Time window */}
              <div className="mb-3">
                <span className="block text-[10px] tracking-[0.3em] text-white/50 mb-2 inline-flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  03 · WHEN&rsquo;S GOOD?
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {WINDOWS.map((w) => {
                    const active = win === w.key;
                    return (
                      <button
                        type="button"
                        key={w.key}
                        onClick={() => setWin(w.key)}
                        className={`min-h-[44px] text-[11px] tracking-[0.22em] uppercase px-3 py-2.5 border transition-colors ${
                          active
                            ? 'border-[#FF3D2E] bg-[#FF3D2E]/10 text-white'
                            : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30'
                        }`}
                        data-testid={`book-call-window-${w.key}`}
                      >
                        {w.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional email — collapsed unless the visitor wants confirmation */}
              <details className="mb-3 group">
                <summary className="list-none cursor-pointer text-[10px] tracking-[0.3em] text-white/50 hover:text-white/80 flex items-center justify-between py-2">
                  <span>+ ADD EMAIL FOR CONFIRMATION</span>
                  <span className="text-white/30 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@brand.com"
                  autoComplete="email"
                  className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-[#FF3D2E] outline-none px-3 py-3 text-white text-base placeholder:text-white/30 transition-colors"
                  data-testid="book-call-email"
                />
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything we should know before the call? (optional)"
                  className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-[#FF3D2E] outline-none px-3 py-3 text-white text-base placeholder:text-white/30 transition-colors resize-none"
                  data-testid="book-call-note"
                />
              </details>

              {errMsg && (
                <p className="mb-3 text-[12px] text-[#FF3D2E]" data-testid="book-call-error">{errMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full inline-flex items-center justify-center gap-2 min-h-[52px] bg-[#FF3D2E] text-white text-[11px] tracking-[0.3em] px-6 py-4 hover:bg-[#ff5446] disabled:opacity-60 transition-colors"
                data-testid="book-call-submit"
              >
                {status === 'sending' ? 'BOOKING…' : 'CONFIRM CALLBACK'}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>

              <p className="mt-3 text-center text-[10px] tracking-[0.22em] text-white/35 uppercase">
                We ring within business hours. No spam, no long forms.
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
